import {LazyTreeViewProps} from "./LazyTreeView";
import {ITidyTable} from "./PublicTidyTable";
import {ITidyTableTransformation} from "./ITidyTableTransformation";
import {Theme} from "@mui/material/styles";
import {ThemeFormatters, ThemeTextFormatter} from "./PublicTheme";
import {ITidyColumn, ITidyNumericColumn} from "./PublicTidyColumn";
import {IPublicWidgetTemplateDefinition} from "./PublicTemplate";
import {ITidyMath} from "./PublicTidyMath";
import {ILogger} from "@ic3/common-api"
import {AppNotification} from "./INotification";
import {WidgetNotificationHandler} from "./IcEvent";
import {TidyRowFilter} from "./PublicTidyTableTypes";
import {FormFieldTidyTableExprType} from "./PublicTemplateForm";

export enum WidgetRenderLayoutStatus {
    RENDERING = "RENDERING",
    RENDERED = "RENDERED",
}

export enum WidgetWarningSeverity {
    WARNING,
    ERROR
}

export enum IContentMessageType { info, error}

export interface IPublicContext {

    logger(): ILogger;

    /**
     * It is the reporting console.
     */
    isAppConsole(): boolean;

    /**
     * It is the dashboard viewer.
     */
    isAppReportViewer(): boolean;

    /**
     * Is an application in viewer mode.
     */
    isAppReportAppViewer(): boolean;

    /**
     * Is an application or dashboard in viewer mode.
     */
    isAppViewer(): boolean;

    /**
     * It is the dashboard editor.
     */
    isAppReportEditor(): boolean;

    /**
     * It is the dashboard editor with the dashboard in preview mode.
     */
    isAppReportEditorPreview(): boolean;

    /**
     * It is the application editor.
     */
    isAppReportAppEditor(): boolean;

    /**
     * It is the gadget editor.
     */
    isAppGadgetEditor(): boolean;

    /**
     * React, returns true when loaded
     */
    useGoogleMapHook(): boolean | Error;

    getTheme(): Theme;

    /**
     * Return the formatter for the current user's locale.
     */
    getThemeFormatter(): ThemeFormatters;

    getUserName(): string;

    getReportName(): string;

    getReportPath(): string;

    getReportFolderName(): string;

    getReportLocale(): string;

    /**
     * @param format Formatter. Defaults to `defaultNumber` from the theme.
     */
    getNumberFormatter(format: ThemeTextFormatter | null | undefined): (value: any | undefined) => string;

    /**
     * @param format Formatter. Defaults to `defaultDate` or `defaultDatetime` from the theme.
     */
    getDateFormatter(format: ThemeTextFormatter | null | undefined): (value: any | undefined) => string;

    /**
     * translate the content of a report (the title of widgets, content of some widgets...). This does use another file
     *
     * aka content localization
     */
    translateContent(content: string, ...args: any[]): string;

    /**
     * localized report application tags (e.g. edit, write, save.. )
     */
    localize(name: string, ...args: any[]): string;

    /**
     * localized report application error tags
     */
    localizeError(name: string, ...args: any[]): string;

    /**
     * Localization for transformations
     */
    localizeTransformationCaption(template: ITidyTableTransformation<any>): { info: string, description?: string };

    localizeFormField(pluginId: string, widgetTemplateId: string, name: string, ...args: any[]): [string, string | undefined, string | undefined];

    /**
     * markdown to sanitized html
     */
    markdownToHtml(markDown: string): string;

    /**
     * A bunch of mathematical functions related to the tidy table.
     */
    tidyMath(): ITidyMath;

    /**
     * Create an expression from a field.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass a string with a single space to make this method return undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     */
    createExpression<T extends FormFieldTidyTableExprType>(fieldType: T, field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter):
        T extends "tidyTableColorRowExpr" | "tidyTableScaleRowExpr" | "tidyTableHtmlRowExpr" | "tidyTableNumericRowExpr" | "tidyTableStringRowExpr" | "tidyTableTextRowExpr" ? ((rowIdx: number) => string) | undefined : (() => string) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableRowTextExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): ((rowIdx: number) => string) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableTextExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, isRowSelected?: TidyRowFilter): (() => string) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableRowMarkdownExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): ((rowIdx: number) => string) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableMarkdownExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): (() => string) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableRowNumericExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): ((rowIdx: number) => number | undefined) | undefined;

    /**
     * same as  createTableRowNumericExpr but returning a string
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableRowNumericStringExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): ((rowIdx: number) => string | undefined) | undefined;

    /**
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableScaleRowNumericExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): ((rowIdx: number) => number | undefined) | undefined;

    /**
     * Not in widget public context because of transformation not applied from a widget context always.
     *
     * @param expression typically coming from a widget field so it cannot be null if there is a default value.
     * To prevent usage of the default, pass an "empty" string that makes this method returns
     * undefined.
     * @param selectedColumns get the row of these columns using `_rowOfSelectedColumns` or `_selectedColumns`.
     * @param isRowSelected function for usage with `totalSelectedOrTotal`. Use tableInter.isSelected(rowIdx).
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableNumericExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): (() => number | undefined) | undefined;

    /**
     * same as  createTableNumericExpr but returning a javascript object
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableNumericStringExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): (() => any | undefined) | undefined;


    /**
     * same as  createTableNumericExpr but returning a javascript object
     *
     * @deprecated will be removed in version >=9.1.0. Use `createExpression` instead.
     */
    createTableNumericJSExpr(field: string, table: ITidyTable, currentColumn: ITidyColumn | undefined, expression: string | undefined, selectedColumns: ITidyColumn[] | undefined, isRowSelected?: TidyRowFilter): (() => any | undefined) | undefined;

    /**
     * Event methods that do not depend on a Table
     *
     * @see ITidyTableInteractionEvent for more
     */

    /**
     * actionName if the action is bound to a channel, clears the event (has no value)
     */
    fireClearEventName(actionName: string): void;

    /**
     * actionName if the action is bound to a channel, send an Mdx Event (value,mdx)
     */
    fireMdxEventName(actionName: string, value: string, mdx: string): void;

    /**
     * Used for publishing app notifications (e.g., print the report).
     */
    fireAppNotification(notification: AppNotification): void;

    formatDate(value: Date | string | undefined | null, format: ThemeTextFormatter | null | undefined, locale?: string): string;

    formatNumber(value: number | string | undefined | null, format: ThemeTextFormatter | null | undefined, locale?: string): string;

    /**
     * A shortcut for formatNumber( theme.formatter.defaultNumber )
     */
    formatAmount(value: number | string | undefined | null, locale?: string): string;

    /**
     * A shortcut for formatNumber( theme.formatter.defaultPercent )
     */
    formatPercent(value: number | string | undefined | null, locale?: string): string;

}

export interface IWidgetStableContext extends IPublicContext {

    getTemplateId(): string;

    getNsId(): string;

    getWidgetId(): string;

    getTemplateId(): string;

    isPrintingMode(): boolean;

    renderWidgetContentMessage(type: IContentMessageType, message: string): any;

    renderLazyTreeView<T>(props: LazyTreeViewProps): T;

    wrapWithTooltip(tooltip: string | undefined, wrappedElement: React.ReactElement): React.ReactElement;

    onWidgetRenderStatusChange(status: WidgetRenderLayoutStatus): void;

    /**
     * Event methods that do not depend on a Table
     *
     * @see ITidyTableInteractionEvent for more
     */

    /**
     * returns true if the actionName in bound to a channel
     */
    firesEvent(actionName: string): boolean;

    /**
     * actionName if the action is bound to a channel, clears the event (has no value)
     */
    fireClearEvent(actionName: string): void;

    /**
     * actionName if the action is bound to a channel, send an Mdx Event (value,mdx)
     */
    fireMdxEvent(actionName: string, value: string, mdx: string): void;

    fireMdxEvents(events: { actionName: string, value: string, mdx: string }[]): void;

    /**
     * Cypress testing purpose, after a rendering of the chart
     */
    incrementWidgetContentRendering(): void;

    getWidgetTemplateDefinition(qualifiedId: string): IPublicWidgetTemplateDefinition<any>;

    resolveWidgetTemplateDefinition(definition: IPublicWidgetTemplateDefinition<any>): Promise<IPublicWidgetTemplateDefinition<any>>;

    /**
     * React -> combines useReduxOwnProps and setReduxOwnProps with a similar syntax to Redux.useState
     *
     * Note it's NOT the initial value but value if undefined
     */
    useReduxOwnPropsState<T>(fieldName: string, valueIfUndefined?: T): [T, (newValue: T | undefined) => void];

    /**
     * React -> useSelector on widgetOwnProps[fieldName]
     */
    useReduxOwnProps<T>(fieldName: string): T;

    /**
     * Redux -> set in Redux value in widgetOwnProps[fieldName]
     */
    setReduxOwnProps(fieldName: string, value: any): void;


    /**
     * Google map
     */

    /**
     * React only, like an use function
     *
     * Loads Google map library with the API key as set
     *
     * return
     *          true when the google map is ready (lib loaded)
     *          error when
     */
    reactUseGoogleMap(): boolean | Error;

    /**
     * React only, like an use function
     *
     * reactUseGoogleMap() + rendered the map into the div referenced by ref with mapOptions
     *
     * @param mapOptions google.maps.MapOptions
     * @param ref ref React.RefObject<HTMLDivElement>
     *
     * return
     *          undefined if loading
     *          {map} google.maps.Map instance when loaded
     *          {error} if loaded on error
     */
    reactUseGoogleMapPlus(mapOptions: any, ref: any): undefined | { map?: any; error?: Error };

    /**
     * Returns latitude and longitude with the same logic as for internal maps
     *
     *
     */
    getMapCoordinates(table: ITidyTable): [ITidyNumericColumn, ITidyNumericColumn] | [];

    getGoogleMapRenderedDelayMS(): number;

    /**
     * Displays an warning icon in the widget header showing the warning when the user hovers the icon.
     * Be sure to clear all warnings before rendering using {@see widgetClearWarnings}
     */
    addWidgetWarning(warning: string, severity?: WidgetWarningSeverity): void;

    /**
     * Clear the warnings in the widget.
     */
    widgetClearWarnings(): void;

    /**
     * Notification
     *
     * like a listener on the components, for React the implementation uses a useEffect, it should
     * NOT be called conditionally.
     */
    useNotification(notifications: WidgetNotificationHandler | WidgetNotificationHandler[], deps: ReadonlyArray<unknown> | undefined): void;

    useUserMenu(handler: (userMenu: string) => any | void, deps: ReadonlyArray<unknown> | undefined): void;

    /**
     * doExport callback is called on each exportToExcel from the userMenu of the widget
     *
     * if the callback returns a tidyTable, this table will be exported
     * if undefined is returned no further action
     *
     * disabled === true, the doExport function is not used
     */
    onExportToExcel(doExport: undefined | (() => ITidyTable | undefined | null), disabled?: boolean): void;

    /**
     * Export the widget data to Excel or CSV.
     * @param table data in the widget (with transformations applied).
     * @param asPivotForExcel If true, convert hierarchical structures to flat columns. Default = false for Excel
     * exports and true for CSV exports.
     * @param rows filter the table by these rows before exporting.
     * @param event object. If `event.format = 'csv'` then this method makes a CSV export. Default is Excel.
     * `event.value` (optional) is the file name of the export.
     */
    exportToExcel(table: ITidyTable, asPivotForExcel: boolean | undefined, rows: number[] | undefined, event?: {
        format?: 'csv' | 'xlsx';
        value?: string;
    }): void;

    useUserMenuWidth(): number | undefined;

    /**
     * Get the (translated) file name for exporting. Does not include file extension. Method returns 'export-file'
     * if no file name is found.
     * @param defaultName if not undefined, method returns this.
     * @param defaultFileName
     */
    getExportFileName(defaultFileName?: string): string;

    /**
     * Returns the report name of the report defining this widget (i.e. could be the embedding)
     */
    getDefiningReportName(): string;

    /**
     * Returns the report path of the report defining this widget (i.e. could be the embedding)
     */
    getDefiningReportPath(): string;

    /**
     * Returns the report folder name of the report defining this widget (i.e. could be the embedding)
     */
    getDefiningReportFolderName(): string;

}

export interface IWidgetPublicContext extends IPublicContext {
    /**
     * Context unlikely to change during the lifecycle of a widget
     * more appropriate to use in a React hook context.
     */
    getStableContext(): IWidgetStableContext;

    /**
     * Get the width of the widget box in pixels.
     */
    getWidgetWidth(): number | undefined;

    /**
     * Get the height of the widget box in pixels.
     */
    getWidgetHeight(): number | undefined;

    getWidgetPageNb(): number;

    /**
     * Returns true if and only if the widgets has a header (from the chart box settings).
     */
    widgetHasHeader(): boolean;

    getBoxSettings(): IPublicWidgetBoxSettings;

    /**
     * True if and only if the widget is invisible, e.g., it has display none.
     */
    isInvisible(): boolean;

}

export interface IPublicWidgetBoxSettings {
    widgetId: string;
    waitingEventOrResult?: string;
    emptyResult?: string;
}

export interface IWidgetEditorPublicContext {

    /**
     * The variants available in the theme for the edited widget.
     */
    getWidgetVariantIds(componentName?: string): string[];

}